import React, { useEffect, useState } from "react";
import {
  useStaffAuth
} from "../context/StaffAuthContext";
import {
  getAllLeaves,
  approveLeave,
  rejectLeave
} from "../api/leaves/leaveApi";
import {
  getAllLeavesPortal,
  approveLeavePortal,
  rejectLeavePortal
} from "../api/leaves/leavePortalApi";
import LeaveCard from "../components/leaves/LeaveCard";
import {
  Calendar,
  RotateCcw,
  Filter
} from "lucide-react";
import axios from "axios";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Skeleton
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const http = axios.create({ baseURL: API, withCredentials: true });

export default function LeaveManagement({ isPortal = false }) {
  const { hasPermission } = useStaffAuth();
  const canManage = isPortal ? hasPermission("LEAVE_MANAGEMENT_FULL") : true;

  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    leaveType: "",
    month: "",
    year: "",
  });

  const toast = useToast();

  const showSuccess = (msg) => {
    toast.success(msg);
  };
  const showError = (msg) => {
    toast.error(msg);
  };

  // Load leave types for filter dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = isPortal
          ? "/api/leave-types-portal/all"
          : "/api/leave-types/all";
        const res = await http.get(path);
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.data || [];
        if (!cancelled) setLeaveTypes(list);
      } catch {
        // non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = isPortal
        ? await getAllLeavesPortal(filters)
        : await getAllLeaves(filters);
      const list = Array.isArray(res) ? res : res.data || res.leaves || [];
      setLeaves(list);
    } catch (err) {
      showError(err.response?.data?.message || "Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal, filters]);

  const handleApprove = async (id, data) => {
    try {
      if (isPortal) await approveLeavePortal(id, data);
      else await approveLeave(id, data);
      showSuccess("Leave request approved");
      fetchLeaves();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to approve leave");
    }
  };

  const handleReject = async (id, data) => {
    try {
      if (isPortal) await rejectLeavePortal(id, data);
      else await rejectLeave(id, data);
      showSuccess("Leave request rejected");
      fetchLeaves();
    } catch (err) {
      showError(err.response?.data?.message || "Failed to reject leave");
    }
  };

  const totalCount = leaves.length;
  const pendingCount = leaves.filter((l) => l.status === "PENDING").length;
  const approvedCount = leaves.filter((l) => l.status === "APPROVED").length;
  const rejectedCount = leaves.filter((l) => l.status === "REJECTED").length;

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
      {/* Header Bar */}
      <div className="flex items-center gap-2.5 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Leave Management
          </h1>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            Review and manage all staff leave requests and allocations.
          </p>
        </div>
      </div>

      {/* Global Alerts */}
      
      

      {/* Summary KPI Cards Grid using Component UI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <Badge variant="secondary" className="mb-1 text-[10px] uppercase">
              Total Requests
            </Badge>
            <span className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{totalCount}</span>
          </CardContent>
        </Card>

        <Card className="border border-amber-200/60 bg-amber-50/20 shadow-none">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <Badge variant="warning" className="mb-1 text-[10px] uppercase">
              Pending
            </Badge>
            <span className="text-2xl font-bold text-amber-800">{pendingCount}</span>
          </CardContent>
        </Card>

        <Card className="border border-emerald-200/60 bg-emerald-50/20 shadow-none">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <Badge variant="success" className="mb-1 text-[10px] uppercase">
              Approved
            </Badge>
            <span className="text-2xl font-bold text-emerald-800">{approvedCount}</span>
          </CardContent>
        </Card>

        <Card className="border border-rose-200/60 bg-rose-50/20 shadow-none">
          <CardContent className="p-4 flex flex-col items-center justify-center">
            <Badge variant="danger" className="mb-1 text-[10px] uppercase">
              Rejected
            </Badge>
            <span className="text-2xl font-bold text-rose-800">{rejectedCount}</span>
          </CardContent>
        </Card>
      </div>

      {/* Filter Toolbar Card using Component UI */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none">
        <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-3">
          <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
            Filter Applications
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">PENDING</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                Leave Type
              </label>
              <select
                value={filters.leaveType}
                onChange={(e) =>
                  setFilters({ ...filters, leaveType: e.target.value })
                }
                className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">All Types</option>
                {leaveTypes.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                Month
              </label>
              <select
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString("en-IN", { month: "long" })}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters({ status: "", leaveType: "", month: "", year: "" })
                }
                leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
                className="w-full text-xs h-[38px] border-neutral-200 dark:border-neutral-800"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Feed */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton height={180} width="100%" />
          <Skeleton height={180} width="100%" />
          <Skeleton height={180} width="100%" />
        </div>
      ) : leaves.length === 0 ? (
        <Card className="border border-dashed border-neutral-200/80 shadow-none p-10 text-center">
          <CardContent className="p-0 text-xs text-neutral-400 dark:text-neutral-500">
            No leave requests found matching selected criteria.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leaves.map((leave) => (
            <LeaveCard
              key={leave._id}
              leave={leave}
              canManage={canManage}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  );
}